import { useContext, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import mixpanel from "mixpanel-browser";

// eslint-disable-next-line import/default, import/no-named-as-default-member
import authenticate from "../../utils/authenticate";

import FullPageLoader from "../../components/full-page-loader";
import { AppContext } from "../../context/AppContext";
import TransferScreen from "../../widgets/transfer-screen";
import sendError from "../../utils/sendError";
import {
  BRIDGE_PRODUCTION,
  BridgeEnv,
  ERROR_CODE_400,
  TRANSFER_FLOW_IN,
  TRANSFER_FLOW_IN_WITH_SOURCE_ACCS,
  TRANSFER_FLOW_OUT,
  TRANSFER_FLOW_STATUS,
  TransferFlow,
} from "../../typings";
import sendOpen from "../../utils/sendOpen";
import parseUrl from "../../utils/parseUrl";
import TransferDetails, { TransferStatusTypes } from "../../class/transferDetails";
import { TransferContext } from "../../context/TransferContext";
import UserDetails from "../../class/userDetails";
import { TransferIntent } from "../../class/transferIntent";
import { sampleTransferInstructions } from "../../utils/__test__/test-data";
import { sampleTransferIntentInResponseIra } from "../../api/mockAPIResponse";
import { sendAppVersionToSentry, setSentryContext } from "../../lib/sentry";

import ErrorPane from "../../widgets/general/error-pane";
import Analytics from "../../utils/productAnalytics";
import axios, { type AxiosInstance } from "axios";

const Home = () => {
  const [isAuth, setIsAuth] = useState<boolean>(false);
  const {
    setAppEnv,
    setPartnerConfig,
    setTransferFlow,
    isAppLoading,
    setIsAppLoading,
    setTransferIntentId,
    setAccessToken,
    setTransferId,
    setClientId,
    setIsWebview,
    setParentUrl,
  } = useContext(AppContext);
  const {
    updateTransferDetails,
    setTransferIntentDetails,
    updateUserDetails,
    setTransferInstructions,
    setUserId,
    setCanStart,
    updateAPIInstance,
  } = useContext(TransferContext);

  /**
   * Fetch
   *  transferFlow
   *  userDetails from transferIntentDetails
   *  transferDetails from transferIntentDetails and see if there is any inprogress transfer
   *  userDetails from internal api if there is in progress transfer

  **/
  const fetchAllDetails = async ({
    apiInstance,
    transferIntentDetails,
  }: {
    env?: BridgeEnv;
    apiInstance: AxiosInstance;
    transferIntentDetails: any;
  }) => {
    let transferDetails;
    let transferFlow = TRANSFER_FLOW_IN;
    if (transferIntentDetails) {
      transferFlow =
        transferIntentDetails.mode === "TRANSFER_OUT"
          ? TRANSFER_FLOW_OUT
          : transferIntentDetails.sourceAccounts.length && transferIntentDetails.sourceAccounts.length > 0
            ? TRANSFER_FLOW_IN_WITH_SOURCE_ACCS
            : TRANSFER_FLOW_IN;

      // transfer intent reference flow
      try {
        const transferDetailsRes = await apiInstance({
          method: "GET",
          url: `/api/userIdentity/transferIntent/${transferIntentDetails.transferIntentId}/transfer`,
        });
        // update transfer details
        transferDetails = transferDetailsRes.data.data;
        updateTransferDetails(TransferDetails.fromAPI(transferDetails));
        setTransferId(transferDetails.transferId);
      } catch (e) {
        // do nothing
      }
    }
    // fetch user details
    const userId = transferIntentDetails.user.userId;
    if (userId) {
      setUserId(userId);
      const userDetailsRes = await apiInstance({
        method: "GET",
        url: `/api/userIdentity/${userId}/userDetails`,
      });
      const userResponseData = userDetailsRes.data.data.userDetails;
      updateUserDetails(
        new UserDetails({
          ...userResponseData,
        }),
      );
    }

    if (
      transferDetails &&
      transferDetails?.transferStatus !== TransferStatusTypes.inProgress &&
      userId &&
      userId !== "undefined"
    ) {
      try {
        const transferInstructionRes = await apiInstance({
          method: "GET",
          url: `/api/userIdentity/${userId}/transfer/transferInstruction`,
        });
        const transferInstructions = transferInstructionRes?.data?.data || [];

        transferFlow = TRANSFER_FLOW_STATUS;

        setTransferInstructions(transferInstructions);
      } catch (e) {
        // do nothing
      }
    }
    setCanStart(true);
    setTransferFlow(transferFlow as TransferFlow);
  };

  useEffect(() => {
    const init = async () => {
      const { accessToken, clientId, transferIntentId, userId, environment, webview, origin } = parseUrl(
        window.location.href,
      );
      sendOpen("init", origin);
      setIsAppLoading(true);
      const parentUrl = document.location.ancestorOrigins
        ? document.location.ancestorOrigins[0]
        : document.referrer
          ? document.referrer
          : undefined;
      const nodeEnv = process.env.NODE_ENV || "development";

      if (nodeEnv !== "test" && !parentUrl && !webview) return;
      setParentUrl(origin);
      sendOpen("loading", origin);
      const {
        ready,
        env = undefined,
        errorCode = undefined,
        errorMessage = undefined,
        transferFlow = undefined,
        clientName = undefined,
        data = {},
      } = await authenticate({
        accessToken,
        clientId,
        transferIntentId,
        userId,
        environment,
      });
      if (errorCode && errorMessage) {
        sendError({ errorCode, errorMessage }, webview, origin);
        sendOpen("error", origin);
        return;
      }

      setIsWebview(webview);

      setAppEnv(env);
      // default mixpanelId for dev
      let mixpanelId = "bd5d25f14e000177792ba847934b1685";
      if (process.env.REACT_APP_ENV === "production" && environment !== "development") {
        // this is the production project id
        mixpanelId = "4923764bd4dfd40af65dc365fd82f44b";
      }
      mixpanel.init(mixpanelId, {
        debug: false,
        track_pageview: false,
        persistence: "localStorage",
        record_sessions_percent: 100,
        record_heatmap_data: true,
        autocapture: {
          click: true,
          capture_text_content: true,
          capture_extra_attrs: ["data-button-title", "data-name", "data-current-url", "name"],
        },
        record_mask_text_selector: "",
      });

      if (env === "BRIDGE_DEMO" || env === "BRIDGE_DEVELOPMENT") {
        mixpanel.opt_out_tracking();
      }

      if (clientName) {
        setPartnerConfig(clientName);
        Analytics.setPartnerName(clientName);
      }

      if (ready) {
        updateAPIInstance({ accessToken, clientId, env: env || "development" });
        const {
          transferIntentDetails = undefined,
          transferInstructionDetails = [], // TODO: STOP HERE: FETCH USER DETAILS DIRECTLY if transfer instruction details are not empty
        } = data;
        if (transferIntentDetails) {
          setTransferIntentDetails(new TransferIntent(transferIntentDetails));
          Analytics.setUserProfiles({
            userName: `${transferIntentDetails.user?.firstName || ""} ${transferIntentDetails.user?.lastName || ""}`,
            userEmail: transferIntentDetails.user?.email,
            partnerName: clientName || "",
            partnerId: clientId,
            transferFlow: TRANSFER_FLOW_IN,
          });
        }
        if (transferInstructionDetails.length > 0) {
          setTransferInstructions(transferInstructionDetails);
        }
        if (!transferIntentDetails && transferInstructionDetails.length === 0) {
          sendError(
            { errorCode: ERROR_CODE_400, errorMessage: "Transfer Intent Id or UserId not found" },
            webview,
            origin,
          );
          sendOpen("error", origin);
          return;
        }
        if (clientId.includes("bridge")) {
          mixpanel.opt_out_tracking();
          updateUserDetails(UserDetails.fromTransferIntent(sampleTransferIntentInResponseIra.user));
          if (transferFlow === TRANSFER_FLOW_STATUS) {
            setTransferInstructions(sampleTransferInstructions);
          }
          if (transferFlow) {
            setTransferFlow(transferFlow);
          } else {
            setTransferFlow(TRANSFER_FLOW_IN);
          }
          setCanStart(true);
        } else {
          setSentryContext({ transferIntentId, userId, clientId });
          sendAppVersionToSentry();
          let updatedTransferIntentId = "";
          if (transferIntentDetails) {
            updatedTransferIntentId = transferIntentDetails.transferIntentId;
          }
          if (!updatedTransferIntentId && transferIntentId && transferIntentId !== "undefined") {
            updatedTransferIntentId = transferIntentId;
          }
          setTransferIntentId(updatedTransferIntentId);
          setUserId(transferIntentDetails?.user.userId || "");
          await fetchAllDetails({
            transferIntentDetails,
            apiInstance: axios.create({
              baseURL:
                env === BRIDGE_PRODUCTION ? process.env.REACT_APP_PROD_API_URL : process.env.REACT_APP_API_BASE_URL,
              headers: {
                Authorization: `Bearer ${accessToken}`,
                ClientId: clientId,
                "X-Auth-Dispatch": "EXTERNAL",
              },
            }),
          });
        }
        setAccessToken(accessToken);
        setClientId(clientId);
        setCanStart(true);
        setIsAppLoading(false);
        setIsAuth(ready);
        sendOpen("success", origin);
      }
    };
    init();
  }, []);

  if (isAppLoading) return <FullPageLoader />;
  return isAuth ? (
    <ErrorBoundary FallbackComponent={ErrorPane}>
      <TransferScreen />
    </ErrorBoundary>
  ) : (
    <FullPageLoader />
  );
};

export default Home;
